import { CompleteProfileModal, DisplayAuthLinks, RedirectUnauthenticated } from "../behaviors";
import { BackNextButton } from "../components/BackNextButton";
import { AppleIdentityBottom } from "../components/healioGlobal/LoginRegistration/AppleIdentityBottom";
import { GoogleIdentityBottom } from "../components/healioGlobal/LoginRegistration/GoogleIdentityBottom";
import { PageContext } from "../service/PageContext";
import { EventUtil } from "../util/EventUtil";
import { UrlUtil } from "../util/UrlUtil";
import { WyanokeUtil } from "../util/WyanokeUtil";
import { initMediaAccessibilityDisclaimers } from "../util/MediaAccessibilityDisclaimer";
import { moduletracking } from "./ModuleTracking";

class WyanokeContext {
  private _pageContext: PageContext | null = null;

  constructor() {
    EventUtil.listenAsyncEvent(BackNextButton.UpdateStepsEvent, () =>
      this.updateActivityState()
    );
    EventUtil.listenAsyncEvent(BackNextButton.AboutToContinueEvent, () =>
      this.updateActivityState()
    );
  }

  public async init(): Promise<void> {
    const pageContext = (this._pageContext = new PageContext());

    const qsMap = UrlUtil.getQueryString();
    if (qsMap.get("logout") !== undefined) {
      WyanokeUtil.logOut();
    }

    $(".logout-user").on("click", () => {
      WyanokeUtil.logOut();
    });

    await new RedirectUnauthenticated().init();

    await CompleteProfileModal.init();

    await pageContext.componentFactory.loadComponents(document.body);
    await new DisplayAuthLinks().init();

    // Global media accessibility disclaimers (Brightcove captions, Libsyn transcripts)
    initMediaAccessibilityDisclaimers();

    moduletracking.TrackLinks();

    AppleIdentityBottom.init(pageContext);
    GoogleIdentityBottom.init(pageContext);

    return this.manageActivityState(pageContext);
  }

  public get pageContext(): PageContext | null {
    return this._pageContext;
  }

  public get isExpired(): boolean {
    return WyanokeContext.isExpired;
  }

  private static get isExperienceEditor(): boolean {
    const pageEditor =
      window.Sitecore &&
      window.Sitecore.PageModes &&
      window.Sitecore.PageModes.PageEditor;

    return Boolean(pageEditor);
  }

  /**
   * True if the activity has expired. Expires is stored as UTC start-of-day;
   * we treat it as a calendar date and expire at end of that day in local time (timezone-independent).
   */
  private static get isExpired(): boolean {
    const activity = window.Activity;
    if (activity?.Expires === undefined || activity?.Expires === null) {
      return false;
    }
    const endDate = new Date(activity.Expires);
    if (isNaN(endDate.getTime())) {
      return false;
    }
    const y = endDate.getUTCFullYear();
    const m = endDate.getUTCMonth();
    const d = endDate.getUTCDate();
    const endOfExpirationDayLocal = new Date(y, m, d, 23, 59, 59, 999);
    return new Date() > endOfExpirationDayLocal;
  }

  private async updateActivityState(): Promise<boolean> {
    const activity = window.Activity;
    const currentStepId = activity ? activity.CurrentStepId : null;
    if (!currentStepId) {
      console.log("Not on current step");

      return true;
    }

    if (WyanokeContext.isExperienceEditor) {
      const msg =
        "Skipping activity state update because the experience " +
        "editor is active.";
      console.log(msg);
      return true;
    }

    if(WyanokeContext.isExpired)  {
      console.log("Activity expired. Step will not be saved");
      return true;
    }

    const pageContext = this._pageContext;
    const service = pageContext ? pageContext.userActivityService : null;
    if (!service) {
      console.log("User activity service is not available.");

      return true;
    }

    console.log("Completing step...");
    await service.completeActivityStep(currentStepId);
    console.log("Step complete");

    return true;
  }

  private async manageActivityState(pageContext: PageContext): Promise<void> {
    const userAgent = navigator.userAgent.toLowerCase();
    const service = pageContext.userActivityService;
    const activity = window.Activity;
    const specialty = activity?.Specialties ? activity.Specialties[0].Title : null;
    const currentStepType = window.Activity?.Steps?.find((s:any) => window.Activity.CurrentStepId === s.Id)?.Type;
    const expiredExempt = ["html", "overview", "cme overview"];
    const linearExempt = ["html", "overview", "cme overview", "healio master class print preview"];

    if (userAgent.includes("elevenlabs")) {
      console.log("Skipping activity state management for elevenlabs bot");
      return;
    }

    if (WyanokeContext.isExperienceEditor) {
      const msg = "Skipping activity state "
        + "management because the experience "
        +  "editor is active.";
      console.log(msg);
      return;
    }

    if(currentStepType && expiredExempt.indexOf(currentStepType.toLowerCase()) != -1) {
      return;
    }

    if (WyanokeContext.isExpired) {

      // Default - cme landing
      let expiredUrl = window.location.href.replace(window.location.pathname, "/cme");

      // Try to find the cme specialty landing
      // and set redirect there
      if (activity && specialty) {
        const specialtyIndex = window.location.pathname.indexOf(
          specialty.replace("/", "-").split(" ").join("-").toLowerCase())
          + specialty.length;
        if (specialtyIndex > -1) {
          expiredUrl = window.location.pathname.substring(0, specialtyIndex);
        }
      }

      // if this is an lms site,
      // set redirect to lms site landing
      if((activity?.LMSName || "").length > 0){
        var segments = window.location.pathname.split('/');
        expiredUrl = '/cme/' + (segments.length < 1 ? "" : segments[1]);
      }

      if(expiredUrl.length > 0) {
        console.warn("Redirecting due to activity expiration date:", expiredUrl);
        window.location.replace(expiredUrl);
      }

      return;
    }

    // force login for outcome survey steps
    const loginqs = UrlUtil.getQueryString().get("login");
    const profile = await WyanokeUtil.loadUserProfile(true, false);
    if (currentStepType && currentStepType.toLowerCase().indexOf("outcome survey") > -1
      && loginqs === undefined
      && (profile?.isLoggedIn === "False" || profile?.isLoggedIn === undefined)) {
      let url = UrlUtil.setQueryString(window.location.pathname, "login", "1");
      console.log("User _must_ be logged in to interact with an outcome survey");
      window.location.replace(url);
      return;
    }

    if(currentStepType && linearExempt.indexOf(currentStepType.toLowerCase()) != -1){
      console.log("linear progression exempt")
      return;
    }

    // ensure linear progression
    const currentStepId = activity ? activity.CurrentStepId : null;
    if (currentStepId) {
      if (service) {
        // check to see if
        // the user has completed
        // the previous step
        const redirectUrl = await service.activityStepRedirectTarget(
          currentStepId
        );

        // if redirectUrl exists
        // and the user is not logged in
        // add the login querystring to the
        // redirect url
        if (redirectUrl) {
          const url = profile
            ? redirectUrl
            : UrlUtil.setQueryString(redirectUrl, "login", "1");

          console.warn("Redirecting due to activty state:", url);

          window.location.replace(url);
        }
      } else {
        console.warn("User activity service not available.");
      }
    }
  }
}

export const context = new WyanokeContext();
$(() => context.init());
